import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Star, Check, Tag } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { PACKAGES } from "../mock/mock";
import PaymentDialog from "./PaymentDialog";

const Packages = ({ limit }) => {
  const items = limit ? PACKAGES.slice(0, limit) : PACKAGES;
  const [selected, setSelected] = useState(null);

  return (
    <section id="packages" className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold tracking-wider uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-600" /> Best Sellers
            </div>
            <h2 className="mt-4 text-3xl md:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Handpicked Tour Packages
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              Curated itineraries with everything included — stay, transport, meals and expert local guides.
            </p>
          </div>
          {limit && (
            <Button asChild variant="outline" className="border-teal-600 text-teal-700 hover:bg-teal-50 self-start">
              <Link to="/packages">View All Packages</Link>
            </Button>
          )}
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <div
              key={p.id}
              className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-2xl hover:shadow-slate-900/10 hover:-translate-y-1 transition duration-300"
            >
              <div className="relative h-52 overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition duration-700" />
                <div className="absolute top-3 left-3">
                  <Badge className="bg-orange-500 hover:bg-orange-500 text-white gap-1">
                    <Tag className="w-3 h-3" /> Save ₹{(p.original - p.price).toLocaleString("en-IN")}
                  </Badge>
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-bold text-slate-900">
                  <Star className="w-3 h-3 fill-orange-500 text-orange-500" />
                  {p.rating} <span className="text-slate-500 font-medium">({p.reviews})</span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-semibold text-teal-700">{p.destination}</span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {p.nights}N / {p.days}D
                  </span>
                </div>
                <h3 className="mt-2 text-lg font-bold text-slate-900 leading-snug">{p.title}</h3>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.inclusions.map((inc) => (
                    <span
                      key={inc}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 text-[11px] font-medium"
                    >
                      <Check className="w-3 h-3" /> {inc}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex items-end justify-between pt-4 border-t border-slate-100">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-slate-900">₹{p.price.toLocaleString("en-IN")}</span>
                      <span className="text-sm text-slate-400 line-through">₹{p.original.toLocaleString("en-IN")}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">per person, taxes included</div>
                  </div>
                  <Button
                    onClick={() => setSelected(p)}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    Book Now
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <PaymentDialog open={!!selected} onClose={() => setSelected(null)} pkg={selected} />
    </section>
  );
};

export default Packages;
